
import * as z from 'zod';

// Define strict schemas for trigger types and their conditions
const candidateStatusTriggerSchema = z.object({
  type: z.literal('candidate_status_change'),
  conditions: z.object({
    fromStatus: z.string().optional(),
    toStatus: z.string().optional(),
    statusList: z.array(z.string()).optional(),
  }),
});

const timeBasedTriggerSchema = z.object({
  type: z.literal('time_based'),
  conditions: z.object({
    schedule: z.string(),
    timezone: z.string().optional(),
    daysOfWeek: z.array(z.number()).optional(),
    timeOfDay: z.string().optional(),
  }),
});

const emailReceivedTriggerSchema = z.object({
  type: z.literal('email_received'),
  conditions: z.object({
    fromEmail: z.string().email().optional(),
    subject: z.string().optional(),
    keywords: z.array(z.string()).optional(),
    attachmentRequired: z.boolean().optional(),
  }),
});

const interviewScheduledTriggerSchema = z.object({
  type: z.literal('interview_scheduled'),
  conditions: z.object({
    interviewType: z.enum(['phone', 'video', 'in-person']).optional(),
    timeframe: z.string().optional(),
    reminderDays: z.number().min(0).max(30).optional(),
  }),
});

// Union of all trigger schemas
const triggerSchema = z.discriminatedUnion('type', [
  candidateStatusTriggerSchema,
  timeBasedTriggerSchema,
  emailReceivedTriggerSchema,
  interviewScheduledTriggerSchema,
]);

// Define strict schemas for action types and their parameters
const createTaskActionSchema = z.object({
  type: z.literal('create_task'),
  parameters: z.object({
    title: z.string().min(1, 'Task title is required'),
    description: z.string().optional(),
    priority: z.enum(['high', 'medium', 'low']).default('medium'),
    dueInDays: z.number().min(0).max(365),
    assignedTo: z.string().optional(),
  }),
});

const sendEmailActionSchema = z.object({
  type: z.literal('send_email'),
  parameters: z.object({
    template: z.string().min(1, 'Email template is required'),
    subject: z.string().optional(),
    delayHours: z.number().min(0).max(168).optional(),
    recipientType: z.enum(['candidate', 'client', 'team']).default('candidate'),
  }),
});

const scheduleFollowUpActionSchema = z.object({
  type: z.literal('schedule_follow_up'),
  parameters: z.object({
    delayDays: z.number().min(1).max(90),
    type: z.enum(['call', 'email', 'meeting']).default('email'),
    notes: z.string().optional(),
  }),
});

const updateStatusActionSchema = z.object({
  type: z.literal('update_status'),
  parameters: z.object({
    newStatus: z.string().min(1, 'New status is required'),
    addNote: z.string().optional(),
    notifyTeam: z.boolean().default(false),
  }),
});

// Union of all action schemas
const actionSchema = z.discriminatedUnion('type', [
  createTaskActionSchema,
  sendEmailActionSchema,
  scheduleFollowUpActionSchema,
  updateStatusActionSchema,
]);

// Main automation rule schema
export const automationRuleSchema = z.object({
  name: z.string().min(1, 'Rule name is required').max(100, 'Rule name too long'),
  description: z.string().min(1, 'Description is required').max(500, 'Description too long'),
  enabled: z.boolean().default(true),
  trigger: triggerSchema,
  actions: z.array(actionSchema).min(1, 'At least one action is required').max(5, 'Too many actions'),
  priority: z.enum(['high', 'medium', 'low']).default('medium'),
});

// Export the inferred type
export type AutomationRuleFormData = z.infer<typeof automationRuleSchema>;

// Validation function with detailed error reporting
export const validateAutomationRule = (data: unknown): { success: boolean; data?: AutomationRuleFormData; errors?: string[] } => {
  try {
    const result = automationRuleSchema.parse(data);
    return { success: true, data: result };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.map(err => `${err.path.join('.')}: ${err.message}`);
      return { success: false, errors };
    }
    return { success: false, errors: ['Invalid rule data'] };
  }
};

// Runtime type guards
export const isTriggerType = (type: string): type is 'candidate_status_change' | 'time_based' | 'email_received' | 'interview_scheduled' => {
  return ['candidate_status_change', 'time_based', 'email_received', 'interview_scheduled'].includes(type);
};

export const isActionType = (type: string): type is 'create_task' | 'send_email' | 'schedule_follow_up' | 'update_status' => {
  return ['create_task', 'send_email', 'schedule_follow_up', 'update_status'].includes(type);
};
